import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { 
  Shield, 
  Heart, 
  Phone, 
  AlertTriangle,
  Baby,
  Clock,
  FileText,
  Users,
  CheckCircle,
  Thermometer,
  Pill,
  ArrowRight,
  MapPin
} from 'lucide-react';

const HealthSafety = () => {
  const healthPolicies = [
    {
      icon: Thermometer,
      title: "Illness Policy",
      summary: "Children with fever, vomiting, or contagious conditions must stay home",
      details: "Children showing symptoms of illness—including fever, vomiting, or contagious conditions—must stay home until they are symptom-free for at least 24 hours. If a child becomes ill during the day, parents are contacted immediately and expected to pick up the child within an hour. We follow Fraser Health guidelines to manage communicable diseases and maintain strict cleaning protocols."
    },
    {
      icon: Pill,
      title: "Medication Administration", 
      summary: "Prescription medications only, with written consent and original containers",
      details: "Prescription medications are administered only with written consent and in their original containers. We do not administer over-the-counter medications unless medically prescribed. All medication must be labeled with the child's name, dosage, and administration schedule. Parents must provide physician instructions for any ongoing medication needs."
    },
    {
      icon: Baby,
      title: "Safe Sleep Practices",
      summary: "Safe sleep checks every 10-15 minutes during rest periods",
      details: "To promote safe sleep, our educators check each sleeping child every 10–15 minutes during nap time. Children sleep in approved cribs or cots, with appropriate bedding for their age. We follow all safe sleep guidelines to reduce the risk of SIDS and ensure peaceful rest for all children."
    },
    {
      icon: Shield,
      title: "Bathroom & Diapering",
      summary: "Respectful, hygienic routines with dignity and privacy",
      details: "Toileting is handled with dignity, support, and hygiene. Children are never left unattended during bathroom routines. Diapering follows strict hygiene procedures - staff wear gloves, sanitize surfaces after each change, and practice handwashing. We collaborate with families during potty training to ensure consistency."
    }
  ];

  const safetyProtocols = [
    {
      icon: Phone,
      title: "Emergency Procedures",
      summary: "Comprehensive emergency plans with regular drills and evacuation sites",
      details: "We conduct monthly fire drills and semi-annual earthquake and lockdown drills. In case of evacuation, children are escorted to Maple Ridge Park, or Fire Hall No. 4 if the primary location is unsafe. Emergency kits and contact lists are always carried. Staff are trained in first aid and emergency response procedures."
    },
    {
      icon: AlertTriangle,
      title: "Incident Reporting",
      summary: "All incidents documented and serious events reported to Fraser Health within 24 hours",
      details: "All injuries and incidents are documented and reported to parents. Minor issues are shared at pickup with written reports. Serious incidents—such as injuries requiring medical attention, missing children, or choking events—are reported to Fraser Health within 24 hours. Parents are contacted immediately by phone."
    },
    {
      icon: Users,
      title: "Missing Child Protocol",
      summary: "Immediate search procedures and emergency service contact",
      details: "If a child goes missing, staff immediately conduct a thorough search of all rooms, playground areas, and exits. If not found within minutes, emergency services and parents are notified immediately. Preventive measures include secure entrances, headcounts during transitions, and proper child-to-staff ratios."
    },
    {
      icon: FileText,
      title: "Child Abuse Reporting",
      summary: "All staff are mandated reporters with zero tolerance for abuse",
      details: "All staff are legally required to report any suspicions of abuse or neglect to Fraser Health or the Ministry of Children and Family Development. We maintain zero tolerance for any form of abuse, neglect, or mistreatment. Staff are trained to recognize signs and respond with professionalism and urgency."
    }
  ];

  const supervisionFeatures = [
    "Visual and auditory supervision at all times",
    "Safe sleep checks every 10-15 minutes during naps",
    "Strategic positioning throughout classrooms and outdoor areas",
    "Low educator-to-child ratios for personalized attention",
    "Security cameras for additional accountability",
    "Thoughtful room layouts for optimal supervision"
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-red-50 via-pink-50 to-purple-50 py-20">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-4xl mx-auto">
            <Badge className="bg-red-500 text-white text-base px-4 py-2 mb-6">
              Safety First
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-purple-600 mb-6">
              Health & Safety
            </h1>
            <p className="text-xl text-gray-700 leading-relaxed">
              Comprehensive policies and procedures designed to keep your child safe, 
              healthy, and secure in our care every single day.
            </p>
          </div>
        </div>
      </section>

      {/* Health Policies */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              Health & Illness Policies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Maintaining a healthy environment is one of our top priorities. 
              Our policies ensure all children can participate safely and comfortably in our program.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {healthPolicies.map((policy, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-red-500">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-3 bg-red-50 rounded-lg group-hover:bg-red-100 transition-colors">
                      <policy.icon className="h-6 w-6 text-red-500" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-navy-primary">{policy.title}</CardTitle>
                      <CardDescription className="text-gray-600">{policy.summary}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-gray-600 text-sm leading-relaxed">{policy.details}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Protocols */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              Safety & Emergency Protocols
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our comprehensive safety protocols ensure we're prepared for any situation 
              while maintaining a warm, nurturing environment for learning and play.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {safetyProtocols.map((protocol, index) => (
              <Card key={index} className="group hover:shadow-lg transition-all duration-300 border-l-4 border-l-blue-primary">
                <CardHeader className="pb-4">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="p-3 bg-blue-50 rounded-lg group-hover:bg-blue-100 transition-colors">
                      <protocol.icon className="h-6 w-6 text-blue-primary" />
                    </div>
                    <div>
                      <CardTitle className="text-lg text-navy-primary">{protocol.title}</CardTitle>
                      <CardDescription className="text-gray-600">{protocol.summary}</CardDescription>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-0">
                  <p className="text-gray-600 text-sm leading-relaxed">{protocol.details}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Supervision & Care */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
                Care & Supervision
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                Children at Maple Kids Academy are always supervised with care, attentiveness, 
                and a deep understanding of their developmental needs. Our team is trained to ensure 
                visual and auditory supervision at all times.
              </p>

              <div className="space-y-4 mb-8">
                {supervisionFeatures.map((feature, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-blue-primary mt-1 flex-shrink-0" />
                    <p className="text-gray-700">{feature}</p>
                  </div>
                ))}
              </div>

              <p className="text-lg text-gray-600 leading-relaxed">
                Through nurturing and presence, our staff build strong relationships with each child, 
                which fosters trust and emotional safety throughout the day. Our video monitoring system 
                provides an added layer of security and accountability.
              </p>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1537655780520-1e392ead81f2?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2MzR8MHwxfHNlYXJjaHwxfHxpbmZhbnRzfGVufDB8fHx8MTc1NjAxNTY2Mnww&ixlib=rb-4.1.0&q=85" 
                alt="Nanny playing blocks with children showing professional childcare supervision" 
                className="rounded-2xl shadow-xl w-full h-96 object-cover"
              />
              <div className="absolute -bottom-6 -right-6 bg-rainbow-green text-white p-4 rounded-full shadow-lg">
                <Shield className="h-8 w-8" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detailed Policy Accordion */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-primary mb-6">
              Detailed Health & Safety Policies
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Click below to read our complete policies and procedures. 
              These guidelines ensure consistent, safe care for all children.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <Accordion type="single" collapsible className="space-y-4">
              <AccordionItem value="cleaning" className="bg-white rounded-lg shadow-md border-0">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-green-50 rounded-lg">
                      <Shield className="h-5 w-5 text-green-500" />
                    </div>
                    <span className="font-semibold text-navy-primary">Cleaning & Sanitation</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600 leading-relaxed">
                    Maple Kids Academy follows rigorous daily cleaning procedures to maintain a clean, healthy, 
                    and germ-free environment. All toys, surfaces, diapering stations, and food preparation areas 
                    are sanitized regularly using only health authority-approved disinfectants. Handwashing is central 
                    to our hygiene routine - staff and children wash hands before meals, after bathroom use, and after 
                    outdoor play. Bedding is laundered weekly or more often if needed. We maintain clear logs of 
                    cleaning tasks to ensure nothing is missed.
                  </p>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="emergency-contacts" className="bg-white rounded-lg shadow-md border-0">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-red-50 rounded-lg">
                      <Phone className="h-5 w-5 text-red-500" />
                    </div>
                    <span className="font-semibold text-navy-primary">Emergency Contact Procedures</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    During emergencies, children are comforted, accounted for with headcounts, and cared for while 
                    awaiting parent pick-up. If a situation requires emergency services, staff will call 911, administer 
                    first aid, and accompany the child to the hospital if needed.
                  </p>
                  <div className="bg-red-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-navy-primary mb-2">Evacuation Sites:</h4>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Primary: Maple Ridge Park</li>
                      <li>• Secondary: Fire Hall No. 4</li>
                      <li>• Emergency kits and contact lists always carried</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="authorized-pickup" className="bg-white rounded-lg shadow-md border-0">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-50 rounded-lg">
                      <Users className="h-5 w-5 text-blue-primary" />
                    </div>
                    <span className="font-semibold text-navy-primary">Authorized Pickup & Safe Code</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    Children are released only to authorized individuals listed in their files. During registration, 
                    families complete a Child Release Authorization Form, listing all individuals permitted to pick up 
                    the child. These individuals must be over 18, present government-issued photo ID, and know the 
                    family's Safe Code.
                  </p>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-navy-primary mb-2">Safety Requirements:</h4>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Must be 18+ years old</li>
                      <li>• Government-issued photo ID required</li>
                      <li>• Must know family's Safe Code</li>
                      <li>• Written authorization only (no verbal/phone)</li>
                      <li>• Staff may refuse release if individual appears impaired</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              <AccordionItem value="behavioral-guidance" className="bg-white rounded-lg shadow-md border-0">
                <AccordionTrigger className="px-6 py-4 hover:no-underline">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-purple-50 rounded-lg">
                      <Heart className="h-5 w-5 text-purple-500" />
                    </div>
                    <span className="font-semibold text-navy-primary">Respectful Behavioral Guidance</span>
                  </div>
                </AccordionTrigger>
                <AccordionContent className="px-6 pb-4">
                  <p className="text-gray-600 leading-relaxed mb-4">
                    We deeply understand that behavior is communication, especially in children under 3 years old. 
                    Rather than using rewards or punishments, we practice respectful guidance rooted in developmental 
                    psychology. We recognize that toddlers often act out due to unmet needs, emotional overwhelm, 
                    or limited communication skills.
                  </p>
                  <div className="bg-purple-50 p-4 rounded-lg">
                    <h4 className="font-semibold text-navy-primary mb-2">Our Approach:</h4>
                    <ul className="text-gray-600 space-y-1">
                      <li>• Co-regulation and emotional support</li>
                      <li>• Positive language and redirection</li>
                      <li>• Consistent routines for security</li>
                      <li>• No timeouts or reward systems</li>
                      <li>• Individualized Support Plans when needed</li>
                      <li>• Close collaboration with families</li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-br from-purple-600 to-pink-500 text-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold mb-6">
                Come Visit Our Happy Place!
              </h2>
              <div className="space-y-4 mb-8">
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <MapPin className="h-5 w-5 mt-1 flex-shrink-0 text-yellow-300" />
                  <div>
                    <p className="font-bold text-lg">10681 248 St, Maple Ridge, BC</p>
                    <p className="text-purple-100">Easy parking for families</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3 bg-white/20 p-4 rounded-xl">
                  <Clock className="h-5 w-5 mt-1 flex-shrink-0 text-green-300" />
                  <div>
                    <p className="font-bold text-lg">Monday - Friday: 7:00 AM - 5:00 PM</p>
                    <p className="text-purple-100">Closed weekends and holidays</p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-yellow-400 hover:bg-yellow-500 text-purple-800 font-bold px-8 py-4 rounded-full shadow-lg hover:shadow-xl transition-all"
                >
                  <Link to="/contact">Book Your Visit</Link>
                </Button>
                <Button 
                  asChild 
                  size="lg" 
                  className="bg-white/20 hover:bg-white/30 text-white border-2 border-white hover:border-yellow-300 font-bold px-8 py-4 rounded-full transition-all"
                >
                  <Link to="/enroll">Join Our Family</Link>
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="bg-white/20 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/30">
                <h3 className="text-2xl font-bold mb-6 text-center">
                  Why Choose Maple Kids?
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center">
                      <Users className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Small groups, big love</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-green-400 rounded-full flex items-center justify-center">
                      <Shield className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Licensed & insured</span>
                  </div>
                  <div className="flex items-center space-x-3">
                    <div className="w-8 h-8 bg-pink-400 rounded-full flex items-center justify-center">
                      <Heart className="h-4 w-4 text-purple-800" />
                    </div>
                    <span className="font-medium">Every child is welcome</span>
                  </div>
                </div>
                
                <div className="mt-6 text-center bg-white/20 p-4 rounded-xl">
                  <p className="text-sm font-medium">Ready to join our family?</p>
                  <p className="text-xs text-purple-100 mt-1">Tours available by appointment</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HealthSafety;